import { AfterViewChecked, AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

import { filter, map, pairwise, throttleTime, Subscription } from 'rxjs';

import { FocusableComponent } from '../focusable';

@Component({
  template: ''
})
export abstract class ScrollListContainerBaseComponent<T>
  extends FocusableComponent
  implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy
{
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  @Input() public items: T[];
  @Input() public isLoading: boolean = false;
  @Input() public isVirtualScrollActive: boolean = false;
  @Input() public abstract itemSize: number;

  private scrollSubscription: Subscription;
  private isViewportListenerInitialized = false;
  private offsetFromBottomToFetch: number;

  public ngAfterViewInit() {
    // Only initialize if not already done
    if (!this.isViewportListenerInitialized) {
      this.initViewportListener();
    }
  }

  public ngAfterViewChecked() {
    // Only initialize if not already done
    if (!this.isViewportListenerInitialized) {
      this.initViewportListener();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    // Only reinitialize if viewport exists and items have changed
    if (changes.items && this.viewport && !this.isViewportListenerInitialized) {
      this.initViewportListener();
    }
  }

  public ngOnDestroy() {
    // Clean up subscription when component is destroyed
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
      this.scrollSubscription = null;
      this.isViewportListenerInitialized = false;
    }
  }

  // Can be overridden by child component
  public abstract onLoadMore(event): void;

  public initViewportListener(bottomOffsetToFetch?: number) {
    this.offsetFromBottomToFetch = bottomOffsetToFetch || this.itemSize * 2;
    // Skip if viewport doesn't exist or listener is already initialized
    if (!this.viewport || this.isViewportListenerInitialized) {
      return;
    }

    // Clean up any existing subscription first
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }

    this.scrollSubscription = this.viewport
      .elementScrolled()
      .pipe(
        map(() => this.viewport.measureScrollOffset('bottom')),
        pairwise(),
        filter(([previousOffset, currentOffset]) => {
          const isScrollingDown = currentOffset < previousOffset;
          const isNearBottom = currentOffset < this.offsetFromBottomToFetch;
          return isScrollingDown && isNearBottom;
        }),
        throttleTime(200)
      )
      .subscribe(event => {
        this.onLoadMore(event);
      });

    this.isViewportListenerInitialized = true;
  }
}
